// src/components/professor/Stats.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { getDatabase, get, ref } from 'firebase/database';
import {
  Activity,
  Award,
  BarChart3,
  Clock3,
  Radio,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';
import Preloader from '../../Preloader';

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const emptyStats = {
  averageGrade: 0,
  highestGrade: 0,
  lowestGrade: 0,
  medianGrade: 0,
  totalStudents: 0,
  totalSessions: 0,
  totalQuestions: 0,
  totalAnswers: 0,
  attendanceRate: 0,
  responseRate: 0,
  answerChanges: 0,
  consistency: 0,
  topStudents: [],
  attentionStudents: [],
  sessionRows: [],
  typeRows: [],
  gradeBands: {},
  questionRows: [],
};

const pct = (value, digits = 1) => Number.isFinite(value) ? Number(value).toFixed(digits) : '0.0';
const sum = (values) => values.reduce((total, value) => total + Number(value || 0), 0);
const average = (values) => values.length ? sum(values) / values.length : 0;
const median = (values) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};
const questionCount = (session) => session.total_questions || Object.keys(session.questions || {}).length;
const sessionLabel = (session, index) => {
  const date = session.start_date_time ? new Date(session.start_date_time) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : `Session ${index + 1}`;
};
const studentName = (student) =>
  `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.email || student.uid || 'Student';

const InsightCard = ({ icon: Icon, label, value, accent }) => (
  <div className="ce-stat-3d">
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--muted-color)' }}>
        {label}
      </span>
      <span className="rounded-lg p-2" style={{ color: accent || 'var(--primary-color)', background: 'var(--alt-bg)' }}>
        <Icon size={17} aria-hidden="true" />
      </span>
    </div>
    <div className="mt-3 text-3xl font-bold" style={{ color: 'var(--text-color)' }}>{value}</div>
  </div>
);

const Stats = ({ students, sessions, grades, classId, theme }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(emptyStats);
  const isDark = theme === 'dark' || document.documentElement.classList.contains('dark');
  const chartTextColor = isDark ? '#f7f7f8' : '#0a0a0a';
  const chartGridColor = isDark ? '#2a2a2e' : '#ececee';

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: chartTextColor } },
    },
    scales: {
      x: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
      y: { beginAtZero: true, ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
    },
  }), [chartGridColor, chartTextColor]);

  const pieOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: chartTextColor } },
    },
  }), [chartTextColor]);

  useEffect(() => {
    let cancelled = false;

    const calculateStats = async () => {
      setLoading(true);
      try {
        const dbRealtime = getDatabase();
        const totalStudents = students.length;
        const totalSessions = sessions.length;
        const totalQuestions = sessions.reduce((total, session) => total + questionCount(session), 0);
        const totalPossiblePerStudent = totalQuestions;

        const [attendanceSnaps, answerSnaps] = await Promise.all([
          Promise.all(sessions.map((session) => get(ref(dbRealtime, `attendance/${classId}/${session.id}/userIds`)))),
          Promise.all(sessions.map((session) => get(ref(dbRealtime, `answers/${classId}/${session.id}`)))),
        ]);

        const attendanceBySession = {};
        attendanceSnaps.forEach((snap, index) => {
          attendanceBySession[sessions[index].id] = snap.exists() ? snap.val() || [] : [];
        });

        const answersBySession = {};
        answerSnaps.forEach((snap, index) => {
          answersBySession[sessions[index].id] = snap.exists() ? snap.val() || {} : {};
        });

        const studentRows = students.map((student) => {
          const studentGrades = grades.students?.[student.uid] || {};
          const points = Object.values(studentGrades).reduce((sessionTotal, sessionData) => {
            return sessionTotal + sum(Object.values(sessionData.points || {}));
          }, 0);
          const percent = totalPossiblePerStudent > 0 ? (points / totalPossiblePerStudent) * 100 : 0;
          const attended = sessions.filter((session) =>
            (attendanceBySession[session.id] || []).includes(student.uid) || Boolean(studentGrades[session.id])
          ).length;
          return {
            uid: student.uid,
            name: studentName(student),
            points,
            percent,
            attended,
            attendanceRate: totalSessions ? (attended / totalSessions) * 100 : 0,
          };
        });

        let totalAnswers = 0;
        let answerChanges = 0;
        const typeStats = {};
        const questionRows = [];

        sessions.forEach((session, sessionIndex) => {
          Object.entries(session.questions || {}).forEach(([questionNumber, question]) => {
            const questionAnswers = answersBySession[session.id]?.[questionNumber] || {};
            const answers = Object.values(questionAnswers);
            const correct = question.correct_answer;
            const type = question.question_type || 'unknown';
            typeStats[type] = typeStats[type] || { type, correct: 0, total: 0 };

            answers.forEach((entry) => {
              const answer = entry?.answer;
              const historyCount = entry?.history ? Object.keys(entry.history).length : 0;
              answerChanges += Math.max(0, historyCount - 1);
              totalAnswers += answer === null || answer === undefined ? 0 : 1;

              let isCorrect = false;
              if (Array.isArray(correct)) {
                const answerArray = Array.isArray(answer) ? answer : [answer];
                isCorrect = correct.length > 0 && correct.every((item) => answerArray.includes(item));
              } else if (correct !== undefined) {
                isCorrect = String(answer || '').trim().toLowerCase() === String(correct).trim().toLowerCase();
              }
              typeStats[type].total += 1;
              if (isCorrect) typeStats[type].correct += 1;
            });

            const possible = Math.max(totalStudents, 1);
            questionRows.push({
              label: `S${sessionIndex + 1} Q${questionNumber}`,
              responses: Object.keys(questionAnswers).length,
              responseRate: (Object.keys(questionAnswers).length / possible) * 100,
              type,
            });
          });
        });

        const percentages = studentRows.map((row) => row.percent);
        const variance = average(percentages.map((value) => Math.pow(value - average(percentages), 2)));
        const consistency = Math.max(0, 100 - Math.sqrt(variance));
        const expectedAnswers = totalStudents * totalQuestions;
        const attendedSlots = sum(Object.values(attendanceBySession).map((list) => Array.isArray(list) ? list.length : 0));
        const attendanceSlots = totalStudents * totalSessions;

        const gradeBands = {
          '90-100': percentages.filter((value) => value >= 90).length,
          '80-89': percentages.filter((value) => value >= 80 && value < 90).length,
          '70-79': percentages.filter((value) => value >= 70 && value < 80).length,
          '60-69': percentages.filter((value) => value >= 60 && value < 70).length,
          '<60': percentages.filter((value) => value < 60).length,
        };

        const sessionRows = sessions.map((session, index) => {
          const sessionAnswers = answersBySession[session.id] || {};
          const answers = Object.values(sessionAnswers).reduce((total, questionAnswers) => {
            return total + Object.keys(questionAnswers || {}).length;
          }, 0);
          const questions = questionCount(session);
          const expected = Math.max(totalStudents * questions, 1);
          return {
            label: sessionLabel(session, index),
            attendance: totalStudents ? ((attendanceBySession[session.id] || []).length / totalStudents) * 100 : 0,
            response: (answers / expected) * 100,
          };
        });

        const typeRows = Object.values(typeStats).map((row) => ({
          label: row.type.replaceAll('_', ' '),
          value: row.total ? (row.correct / row.total) * 100 : 0,
          total: row.total,
        }));

        const sortedStudents = [...studentRows].sort((a, b) => b.percent - a.percent);

        if (!cancelled) {
          setStats({
            averageGrade: average(percentages),
            highestGrade: percentages.length ? Math.max(...percentages) : 0,
            lowestGrade: percentages.length ? Math.min(...percentages) : 0,
            medianGrade: median(percentages),
            totalStudents,
            totalSessions,
            totalQuestions,
            totalAnswers,
            attendanceRate: attendanceSlots ? (attendedSlots / attendanceSlots) * 100 : 0,
            responseRate: expectedAnswers ? (totalAnswers / expectedAnswers) * 100 : 0,
            answerChanges,
            consistency,
            topStudents: sortedStudents.slice(0, 5),
            attentionStudents: sortedStudents.slice(-5).reverse(),
            sessionRows,
            typeRows,
            gradeBands,
            questionRows: [...questionRows].sort((a, b) => a.responseRate - b.responseRate).slice(0, 6),
          });
        }
      } catch (error) {
        console.error('Error calculating stats:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    calculateStats();
    return () => {
      cancelled = true;
    };
  }, [classId, grades, sessions, students]);

  const sessionTrendData = {
    labels: stats.sessionRows.map((row) => row.label),
    datasets: [
      {
        label: 'Attendance',
        data: stats.sessionRows.map((row) => row.attendance),
        borderColor: '#1f8a4c',
        backgroundColor: 'rgba(31, 138, 76, 0.14)',
        tension: 0.35,
        fill: true,
      },
      {
        label: 'Response',
        data: stats.sessionRows.map((row) => row.response),
        borderColor: '#c43a3a',
        backgroundColor: 'rgba(196, 58, 58, 0.14)',
        tension: 0.35,
        fill: true,
      },
    ],
  };

  const typeData = {
    labels: stats.typeRows.map((row) => row.label),
    datasets: [
      {
        label: 'Accuracy',
        data: stats.typeRows.map((row) => row.value),
        backgroundColor: '#c43a3a',
        borderRadius: 8,
      },
    ],
  };

  const gradeBandData = {
    labels: Object.keys(stats.gradeBands),
    datasets: [
      {
        data: Object.values(stats.gradeBands),
        backgroundColor: ['#1f8a4c', '#1f5fbf', '#b97a1d', '#d65656', '#b42318'],
      },
    ],
  };

  if (loading) return <Preloader />;

  return (
    <div className="ce-analytics-page">
      <div className="ce-analytics-hero">
        <div>
          <p className="ce-eyebrow">Class pulse</p>
          <h3>Overall student analytics</h3>
        </div>
        <div className="ce-performance-orb" style={{ '--score': `${Math.min(stats.averageGrade, 100) * 3.6}deg` }}>
          <span>{pct(stats.averageGrade, 0)}%</span>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <InsightCard icon={Users} label="Students" value={stats.totalStudents} />
        <InsightCard icon={BarChart3} label="Avg grade" value={`${pct(stats.averageGrade)}%`} />
        <InsightCard icon={Radio} label="Response" value={`${pct(stats.responseRate)}%`} />
        <InsightCard icon={Activity} label="Attendance" value={`${pct(stats.attendanceRate)}%`} />
        <InsightCard icon={Target} label="Median" value={`${pct(stats.medianGrade)}%`} />
        <InsightCard icon={Award} label="Highest" value={`${pct(stats.highestGrade)}%`} />
        <InsightCard icon={TrendingUp} label="Consistency" value={`${pct(stats.consistency)}%`} />
        <InsightCard icon={Clock3} label="Answer changes" value={stats.answerChanges} />
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[1.45fr_1fr]">
        <div className="ce-analytics-card">
          <h4>Session momentum</h4>
          <div className="h-[320px]">
            <Line data={sessionTrendData} options={chartOptions} />
          </div>
        </div>
        <div className="ce-analytics-card">
          <h4>Grade bands</h4>
          <div className="h-[320px]">
            <Pie data={gradeBandData} options={pieOptions} />
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-2">
        <div className="ce-analytics-card">
          <h4>Question type accuracy</h4>
          <div className="h-[300px]">
            <Bar data={typeData} options={chartOptions} />
          </div>
        </div>
        <div className="ce-analytics-card">
          <h4>Lowest response questions</h4>
          <div className="space-y-3">
            {stats.questionRows.map((row) => (
              <div key={row.label} className="ce-progress-row">
                <div className="flex items-center justify-between gap-3">
                  <span>{row.label}</span>
                  <span>{pct(row.responseRate)}%</span>
                </div>
                <div className="ce-progress-track">
                  <div style={{ width: `${Math.min(row.responseRate, 100)}%` }} />
                </div>
              </div>
            ))}
            {stats.questionRows.length === 0 && <div className="ce-empty">No question response data yet.</div>}
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-2">
        <div className="ce-analytics-card">
          <h4>Top performers</h4>
          <div className="space-y-2">
            {stats.topStudents.map((row, index) => (
              <div key={row.uid} className="ce-rank-row">
                <span>{index + 1}</span>
                <strong>{row.name}</strong>
                <em>{pct(row.percent)}%</em>
              </div>
            ))}
          </div>
        </div>
        <div className="ce-analytics-card">
          <h4>Needs attention</h4>
          <div className="space-y-2">
            {stats.attentionStudents.map((row, index) => (
              <div key={row.uid} className="ce-rank-row">
                <span>{index + 1}</span>
                <strong>{row.name}</strong>
                <em>{pct(row.percent)}%</em>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Stats;
